import React, { useEffect, useState } from 'react';
import { X, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  getLeadTypeLabel,
  PRE_DEAL_STATUS_OPTIONS_BY_TYPE,
  formatLeadStatusLabel,
} from './LeadDetailsContent';
import { adminDialogPanel, adminIconBtn, adminOverlay } from '@/components/admin/adminUi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const inputClass =
  "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
const selectClass =
  "w-full bg-black border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-black [&>option]:text-white";
const labelClass = "block text-xs font-['Manrope'] text-white/60 mb-1.5";

function buildInitialForm(lead) {
  const d = lead.details || {};
  const base = {
    name: lead.name || '',
    email: lead.email || '',
    phone: lead.phone || '',
    status: PRE_DEAL_STATUS_OPTIONS_BY_TYPE[lead.type]?.includes(lead.status)
      ? lead.status
      : (PRE_DEAL_STATUS_OPTIONS_BY_TYPE[lead.type]?.[0] || 'contacted'),
  };

  if (lead.type === 'contact') {
    return { ...base, message: d.message || '' };
  }
  if (lead.type === 'vehicle_inquiry') {
    return {
      ...base,
      vehicle_info: d.vehicle_info || '',
      preferred_contact: d.preferred_contact || 'email',
      message: d.message || '',
    };
  }
  if (lead.type === 'financing') {
    const a = d.applicant || {};
    const e = d.employment || {};
    const cb = d.coBuyer || {};
    const cbi = cb.info || {};
    const cbe = cb.employment || {};
    return {
      ...base,
      applicant: {
        firstName: a.firstName || '',
        middleName: a.middleName || '',
        lastName: a.lastName || '',
        dateOfBirth: a.dateOfBirth || '',
        ssnLast4: a.ssn_last4 || '',
        ssnReplace: '',
        mobileNumber: a.mobileNumber || lead.phone || '',
        homeNumber: a.homeNumber || '',
        email: a.email || lead.email || '',
        addressLine1: a.addressLine1 || '',
        addressLine2: a.addressLine2 || '',
        city: a.city || '',
        state: a.state || '',
        zip: a.zip || '',
        driverLicenseLast4: a.driverLicenseLast4 || '',
        driverLicenseReplace: '',
        driverLicenseState: a.driverLicenseState || '',
        dlIssuingCounty: a.dlIssuingCounty || '',
        driverLicenseExpiry: a.driverLicenseExpiry || '',
        residenceType: a.residenceType || '',
        rentMortgage: a.rentMortgage || '',
        residenceYears: a.residenceYears || '',
        residenceMonths: a.residenceMonths || '',
      },
      employment: {
        employer: e.employer || '',
        employerType: e.employerType || '',
        occupation: e.occupation || '',
        monthlyIncome: e.monthlyIncome || '',
        workPhone: e.workPhone || '',
        jobYears: e.jobYears || '',
        jobMonths: e.jobMonths || '',
        addressLine1: e.addressLine1 || '',
        addressLine2: e.addressLine2 || '',
        city: e.city || '',
        state: e.state || '',
        zip: e.zip || '',
      },
      hasCoBuyer: Boolean(cbi.firstName || cbi.lastName || cbi.email || cbe.employer),
      coBuyer: {
        info: {
          firstName: cbi.firstName || '',
          lastName: cbi.lastName || '',
          dateOfBirth: cbi.dateOfBirth || '',
          ssnLast4: cbi.ssn_last4 || '',
          ssnReplace: '',
          mobileNumber: cbi.mobileNumber || '',
          email: cbi.email || '',
          addressLine1: cbi.addressLine1 || '',
          city: cbi.city || '',
          state: cbi.state || '',
          zip: cbi.zip || '',
        },
        employment: {
          employer: cbe.employer || '',
          occupation: cbe.occupation || '',
          monthlyIncome: cbe.monthlyIncome || '',
        },
      },
      additionalComments: d.additionalComments || '',
    };
  }
  if (lead.type === 'trade_in') {
    return {
      ...base,
      year: d.year || (d.vehicle || '').split(' ')[0] || '',
      make: d.make || '',
      model: d.model || '',
      mileage: d.mileage || '',
      vin: d.vin || '',
      financed: d.financed || '',
      accidents: d.accidents || '',
      description: d.description || '',
    };
  }
  if (lead.type === 'prequalification') {
    return {
      ...base,
      address: d.address || '',
      dateOfBirth: d.dateOfBirth || '',
      jobDuration: d.jobDuration || '',
      monthlyIncome: d.monthlyIncome || '',
      creditScore: d.creditScore || '',
      interestedVehicle: d.interestedVehicle || '',
      budget: d.budget || '',
      downPayment: d.downPayment || '',
      payTaxesUpfront: d.payTaxesUpfront || '',
    };
  }
  return base;
}

function buildPayload(lead, form) {
  const payload = {
    type: lead.type,
    status: form.status,
    name: form.name,
    email: form.email,
    phone: form.phone,
    details: {},
  };

  if (lead.type === 'contact') {
    payload.details = { message: form.message };
  } else if (lead.type === 'vehicle_inquiry') {
    payload.details = {
      message: form.message,
      vehicle_info: form.vehicle_info,
      preferred_contact: form.preferred_contact,
    };
  } else if (lead.type === 'financing') {
    const a = { ...(form.applicant || {}) };
    const applicant = { ...a };
    delete applicant.ssnLast4;
    delete applicant.ssnReplace;
    delete applicant.driverLicenseLast4;
    delete applicant.driverLicenseReplace;
    if ((a.ssnReplace || '').replace(/\D/g, '').length === 9) {
      applicant.ssn = a.ssnReplace;
    }
    if ((a.driverLicenseReplace || '').trim().length > 4) {
      applicant.driverLicenseNumber = a.driverLicenseReplace;
    }
    const coBuyer = form.hasCoBuyer ? { ...(form.coBuyer || {}) } : null;
    if (coBuyer?.info) {
      const info = { ...coBuyer.info };
      delete info.ssnLast4;
      if ((info.ssnReplace || '').replace(/\D/g, '').length === 9) {
        info.ssn = info.ssnReplace;
      } else {
        delete info.ssn;
      }
      delete info.ssnReplace;
      coBuyer.info = info;
    }
    payload.name = `${a.firstName || ''} ${a.lastName || ''}`.trim();
    payload.email = a.email || '';
    payload.phone = a.mobileNumber || '';
    payload.details = {
      applicant,
      employment: form.employment || {},
      coBuyer,
      additionalComments: form.additionalComments || '',
    };
  } else if (lead.type === 'trade_in') {
    payload.details = {
      year: form.year,
      make: form.make,
      model: form.model,
      mileage: form.mileage,
      vin: form.vin,
      financed: form.financed,
      accidents: form.accidents,
      description: form.description,
    };
  } else if (lead.type === 'prequalification') {
    payload.details = {
      address: form.address,
      dateOfBirth: form.dateOfBirth,
      jobDuration: form.jobDuration,
      monthlyIncome: form.monthlyIncome,
      creditScore: form.creditScore,
      interestedVehicle: form.interestedVehicle,
      budget: form.budget,
      downPayment: form.downPayment,
      payTaxesUpfront: form.payTaxesUpfront,
    };
  }

  return payload;
}

export default function LeadEditModal({
  isOpen,
  onClose,
  lead,
  token,
  onSaved,
  onDeleted,
  saveUrl,
  hideStatus = false,
  hideDelete = false,
  title = 'Edit Lead',
}) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (lead) setForm(buildInitialForm(lead));
  }, [lead]);

  if (!isOpen || !lead || !form) return null;

  const statusOptions = PRE_DEAL_STATUS_OPTIONS_BY_TYPE[lead.type] || ['contacted', 'closed'];

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setNested = (group, key, value) => setForm((prev) => ({
    ...prev,
    [group]: { ...(prev[group] || {}), [key]: value },
  }));
  const setCoBuyer = (section, key, value) => setForm((prev) => ({
    ...prev,
    coBuyer: {
      ...(prev.coBuyer || {}),
      [section]: { ...((prev.coBuyer || {})[section] || {}), [key]: value },
    },
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = saveUrl || `${BACKEND_URL}/api/admin/leads/${lead.id}`;
      const res = await axios.put(
        url,
        buildPayload(lead, form),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Customer information updated');
      onSaved?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/leads/${lead.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Lead deleted');
      onDeleted?.(lead);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete lead');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={`${adminOverlay('z-[70]')}`} data-testid="lead-edit-modal">
      <div className={adminDialogPanel('max-w-4xl')}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <div>
            <p className="text-xs font-['Manrope'] text-white/40 mb-1">{getLeadTypeLabel(lead.type)}</p>
            <h2 className="text-lg font-['Unbounded'] font-bold text-white">{title}</h2>
          </div>
          <button type="button" onClick={onClose} className={adminIconBtn} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!hideStatus && (
          <div>
            <label className={labelClass}>Lead Status</label>
            <select value={form.status} onChange={(e) => set('status', e.target.value)} className={selectClass}>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{formatLeadStatusLabel(s)}</option>
              ))}
            </select>
            <p className="text-[11px] font-['Manrope'] text-white/35 mt-1">
              Use Contacted after reaching out. Deal pipeline statuses apply automatically after conversion.
            </p>
          </div>
          )}

          {lead.type !== 'financing' && (
            <>
              <div>
                <label className={labelClass}>Name</label>
                <input value={form.name} onChange={(e) => set('name', e.target.value)} className={inputClass} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Email</label>
                  <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputClass} />
                </div>
              </div>
            </>
          )}

          {lead.type === 'contact' && (
            <div>
              <label className={labelClass}>Message</label>
              <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={4} className={inputClass} />
            </div>
          )}

          {lead.type === 'vehicle_inquiry' && (
            <>
              <div>
                <label className={labelClass}>Vehicle Interest</label>
                <input value={form.vehicle_info} onChange={(e) => set('vehicle_info', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Preferred Contact</label>
                <select value={form.preferred_contact} onChange={(e) => set('preferred_contact', e.target.value)} className={selectClass}>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Message</label>
                <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={3} className={inputClass} />
              </div>
            </>
          )}

          {lead.type === 'financing' && form.applicant && (
            <>
              <div className="space-y-3">
                <h3 className="text-xs font-['Unbounded'] font-semibold text-blue-400 uppercase tracking-wider">Applicant Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>First Name</label>
                    <input value={form.applicant.firstName} onChange={(e) => setNested('applicant', 'firstName', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Middle Name</label>
                    <input value={form.applicant.middleName} onChange={(e) => setNested('applicant', 'middleName', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Last Name</label>
                    <input value={form.applicant.lastName} onChange={(e) => setNested('applicant', 'lastName', e.target.value)} className={inputClass} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Date of Birth</label>
                    <input value={form.applicant.dateOfBirth} onChange={(e) => setNested('applicant', 'dateOfBirth', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>SSN (last 4)</label>
                    <input value={form.applicant.ssnLast4 ? `***-**-${form.applicant.ssnLast4}` : 'Not on file'} readOnly className={`${inputClass} text-white/50`} />
                    <input
                      value={form.applicant.ssnReplace}
                      onChange={(e) => setNested('applicant', 'ssnReplace', e.target.value)}
                      placeholder="Replace full SSN (optional)"
                      className={`${inputClass} mt-1.5`}
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Mobile Phone</label>
                    <input value={form.applicant.mobileNumber} onChange={(e) => setNested('applicant', 'mobileNumber', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Home Phone</label>
                    <input value={form.applicant.homeNumber} onChange={(e) => setNested('applicant', 'homeNumber', e.target.value)} className={inputClass} />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Email</label>
                    <input type="email" value={form.applicant.email} onChange={(e) => setNested('applicant', 'email', e.target.value)} className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Address Line 1</label>
                  <input value={form.applicant.addressLine1} onChange={(e) => setNested('applicant', 'addressLine1', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Address Line 2</label>
                  <input value={form.applicant.addressLine2} onChange={(e) => setNested('applicant', 'addressLine2', e.target.value)} className={inputClass} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>City</label>
                    <input value={form.applicant.city} onChange={(e) => setNested('applicant', 'city', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>State</label>
                    <input value={form.applicant.state} onChange={(e) => setNested('applicant', 'state', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>ZIP</label>
                    <input value={form.applicant.zip} onChange={(e) => setNested('applicant', 'zip', e.target.value)} className={inputClass} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Driver License # (last 4)</label>
                    <input value={form.applicant.driverLicenseLast4 ? `••••${form.applicant.driverLicenseLast4}` : 'Not on file'} readOnly className={`${inputClass} text-white/50`} />
                    <input
                      value={form.applicant.driverLicenseReplace}
                      onChange={(e) => setNested('applicant', 'driverLicenseReplace', e.target.value)}
                      placeholder="Replace full DL number (optional)"
                      className={`${inputClass} mt-1.5`}
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>DL State</label>
                    <input value={form.applicant.driverLicenseState} onChange={(e) => setNested('applicant', 'driverLicenseState', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>DL Issuing County</label>
                    <input value={form.applicant.dlIssuingCounty} onChange={(e) => setNested('applicant', 'dlIssuingCounty', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>DL Expiry</label>
                    <input value={form.applicant.driverLicenseExpiry} onChange={(e) => setNested('applicant', 'driverLicenseExpiry', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Residence Type</label>
                    <input value={form.applicant.residenceType} onChange={(e) => setNested('applicant', 'residenceType', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Rent/Mortgage</label>
                    <input value={form.applicant.rentMortgage} onChange={(e) => setNested('applicant', 'rentMortgage', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Years at Residence</label>
                    <input value={form.applicant.residenceYears} onChange={(e) => setNested('applicant', 'residenceYears', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Months at Residence</label>
                    <input value={form.applicant.residenceMonths} onChange={(e) => setNested('applicant', 'residenceMonths', e.target.value)} className={inputClass} />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-white/10">
                <h3 className="text-xs font-['Unbounded'] font-semibold text-green-400 uppercase tracking-wider">Employment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Employer</label>
                    <input value={form.employment.employer} onChange={(e) => setNested('employment', 'employer', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Employer Type</label>
                    <input value={form.employment.employerType} onChange={(e) => setNested('employment', 'employerType', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Occupation</label>
                    <input value={form.employment.occupation} onChange={(e) => setNested('employment', 'occupation', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Monthly Income</label>
                    <input value={form.employment.monthlyIncome} onChange={(e) => setNested('employment', 'monthlyIncome', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Work Phone</label>
                    <input value={form.employment.workPhone} onChange={(e) => setNested('employment', 'workPhone', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Years at Job</label>
                    <input value={form.employment.jobYears} onChange={(e) => setNested('employment', 'jobYears', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Months at Job</label>
                    <input value={form.employment.jobMonths} onChange={(e) => setNested('employment', 'jobMonths', e.target.value)} className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Work Address Line 1</label>
                  <input value={form.employment.addressLine1} onChange={(e) => setNested('employment', 'addressLine1', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Work Address Line 2</label>
                  <input value={form.employment.addressLine2} onChange={(e) => setNested('employment', 'addressLine2', e.target.value)} className={inputClass} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>City</label>
                    <input value={form.employment.city} onChange={(e) => setNested('employment', 'city', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>State</label>
                    <input value={form.employment.state} onChange={(e) => setNested('employment', 'state', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>ZIP</label>
                    <input value={form.employment.zip} onChange={(e) => setNested('employment', 'zip', e.target.value)} className={inputClass} />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-white/10">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(form.hasCoBuyer)}
                    onChange={(e) => set('hasCoBuyer', e.target.checked)}
                    className="accent-orange-500"
                  />
                  <span className="text-xs font-['Unbounded'] font-semibold text-orange-400 uppercase tracking-wider">Co-Buyer Information</span>
                </label>
                {form.hasCoBuyer && form.coBuyer && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>First Name</label>
                        <input value={form.coBuyer.info.firstName} onChange={(e) => setCoBuyer('info', 'firstName', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Last Name</label>
                        <input value={form.coBuyer.info.lastName} onChange={(e) => setCoBuyer('info', 'lastName', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Date of Birth</label>
                        <input value={form.coBuyer.info.dateOfBirth} onChange={(e) => setCoBuyer('info', 'dateOfBirth', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>SSN</label>
                        <input value={form.coBuyer.info.ssnLast4 ? `***-**-${form.coBuyer.info.ssnLast4}` : 'Not on file'} readOnly className={`${inputClass} text-white/50`} />
                        <input
                          value={form.coBuyer.info.ssnReplace || ''}
                          onChange={(e) => setCoBuyer('info', 'ssnReplace', e.target.value)}
                          placeholder="Replace full SSN (optional)"
                          className={`${inputClass} mt-1.5`}
                          autoComplete="off"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Mobile Phone</label>
                        <input value={form.coBuyer.info.mobileNumber} onChange={(e) => setCoBuyer('info', 'mobileNumber', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Email</label>
                        <input type="email" value={form.coBuyer.info.email} onChange={(e) => setCoBuyer('info', 'email', e.target.value)} className={inputClass} />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelClass}>Address</label>
                        <input value={form.coBuyer.info.addressLine1} onChange={(e) => setCoBuyer('info', 'addressLine1', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>City</label>
                        <input value={form.coBuyer.info.city} onChange={(e) => setCoBuyer('info', 'city', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>State</label>
                        <input value={form.coBuyer.info.state} onChange={(e) => setCoBuyer('info', 'state', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>ZIP</label>
                        <input value={form.coBuyer.info.zip} onChange={(e) => setCoBuyer('info', 'zip', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Employer</label>
                        <input value={form.coBuyer.employment.employer} onChange={(e) => setCoBuyer('employment', 'employer', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Occupation</label>
                        <input value={form.coBuyer.employment.occupation} onChange={(e) => setCoBuyer('employment', 'occupation', e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Monthly Income</label>
                        <input value={form.coBuyer.employment.monthlyIncome} onChange={(e) => setCoBuyer('employment', 'monthlyIncome', e.target.value)} className={inputClass} />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="pt-4 border-t border-white/10">
                <label className={labelClass}>Additional Comments</label>
                <textarea value={form.additionalComments} onChange={(e) => set('additionalComments', e.target.value)} rows={3} className={inputClass} />
              </div>
            </>
          )}

          {lead.type === 'trade_in' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input placeholder="Year" value={form.year} onChange={(e) => set('year', e.target.value)} className={inputClass} />
                <input placeholder="Make" value={form.make} onChange={(e) => set('make', e.target.value)} className={inputClass} />
                <input placeholder="Model" value={form.model} onChange={(e) => set('model', e.target.value)} className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Mileage" value={form.mileage} onChange={(e) => set('mileage', e.target.value)} className={inputClass} />
                <input placeholder="VIN" value={form.vin} onChange={(e) => set('vin', e.target.value)} className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Financed" value={form.financed} onChange={(e) => set('financed', e.target.value)} className={inputClass} />
                <input placeholder="Accidents" value={form.accidents} onChange={(e) => set('accidents', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} className={inputClass} />
              </div>
            </>
          )}

          {lead.type === 'prequalification' && (
            <>
              <input placeholder="Address" value={form.address} onChange={(e) => set('address', e.target.value)} className={inputClass} />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Date of Birth" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} className={inputClass} />
                <input placeholder="Job Duration" value={form.jobDuration} onChange={(e) => set('jobDuration', e.target.value)} className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Monthly Income" value={form.monthlyIncome} onChange={(e) => set('monthlyIncome', e.target.value)} className={inputClass} />
                <input placeholder="Credit Score" value={form.creditScore} onChange={(e) => set('creditScore', e.target.value)} className={inputClass} />
              </div>
              <input placeholder="Vehicle Interest" value={form.interestedVehicle} onChange={(e) => set('interestedVehicle', e.target.value)} className={inputClass} />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Budget" value={form.budget} onChange={(e) => set('budget', e.target.value)} className={inputClass} />
                <input placeholder="Down Payment" value={form.downPayment} onChange={(e) => set('downPayment', e.target.value)} className={inputClass} />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-white/10">
          <div>
            {!hideDelete && !confirmDelete && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={saving || deleting}
                className="flex items-center gap-2 px-3 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 text-sm font-['Manrope'] disabled:opacity-50"
                aria-label="Delete lead"
                data-testid="lead-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
            {!hideDelete && confirmDelete && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-['Manrope'] text-red-300">Delete this lead?</span>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-3 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
                  data-testid="lead-delete-confirm-btn"
                >
                  {deleting ? 'Deleting...' : 'Yes, delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  className="px-3 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']"
                >
                  No
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || deleting}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}